// ---------------------------------------------------------------------------
// Merchant Risk Dashboard — risk score visualization
// ---------------------------------------------------------------------------

import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RiskFactor {
  category: string;
  score: number;
  weight: number;
  details: string;
}

interface MerchantRiskDashboardProps {
  overallScore: number;
  factors: RiskFactor[];
  lastAssessedAt: string | null;
  nextReviewAt: string | null;
}

// ---------------------------------------------------------------------------
// Risk tier styling
// ---------------------------------------------------------------------------

function getRiskTierLabel(score: number): string {
  if (score <= 30) return 'Low';
  if (score <= 60) return 'Medium';
  if (score <= 80) return 'High';
  return 'Critical';
}

function getRiskTierColor(score: number): string {
  if (score <= 30) return 'text-primary';
  if (score <= 60) return 'text-warning';
  if (score <= 80) return 'text-warning';
  return 'text-danger';
}

function getGaugeColor(score: number): string {
  if (score <= 30) return 'bg-primary-tint0';
  if (score <= 60) return 'bg-warning';
  if (score <= 80) return 'bg-warning';
  return 'bg-danger-tint0';
}

function getBarColor(score: number): string {
  if (score <= 30) return 'bg-primary';
  if (score <= 60) return 'bg-warning';
  if (score <= 80) return 'bg-warning';
  return 'bg-danger';
}

const CATEGORY_LABELS: Record<string, string> = {
  transaction_pattern: 'Transaction Pattern',
  chargeback_rate: 'Chargeback Rate',
  industry_risk: 'Industry Risk',
  geographic_risk: 'Geographic Risk',
  compliance_history: 'Compliance History',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MerchantRiskDashboard({
  overallScore,
  factors,
  lastAssessedAt,
  nextReviewAt,
}: MerchantRiskDashboardProps) {
  const f = useFormatters();
  const tierLabel = getRiskTierLabel(overallScore);
  const tierColor = getRiskTierColor(overallScore);
  const gaugeColor = getGaugeColor(overallScore);

  return (
    <div className="space-y-6">
      {/* Overall score gauge */}
      <div className="rounded-sm border border-hairline bg-surface-raised p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase text-ink-muted">
          Overall Risk Score
        </h3>
        <div className="flex items-center gap-6">
          {/* Circular gauge */}
          <div className="relative flex h-24 w-24 shrink-0 items-center justify-center">
            <svg className="h-24 w-24" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="8"
              />
              {/* Score arc */}
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeDasharray={`${(overallScore / 100) * 264} 264`}
                strokeDashoffset="0"
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
                className={tierColor}
              />
            </svg>
            <span className={`absolute text-2xl font-bold ${tierColor}`}>
              {overallScore}
            </span>
          </div>

          <div>
            <p className={`text-lg font-semibold ${tierColor}`}>
              {tierLabel} Risk
            </p>
            {lastAssessedAt && (
              <p className="mt-1 text-xs text-ink-muted">
                Last assessed: {f.date(lastAssessedAt, { dateStyle: 'medium' })}
              </p>
            )}
            {nextReviewAt && (
              <p className="text-xs text-ink-muted">
                Next review: {f.date(nextReviewAt, { dateStyle: 'medium' })}
              </p>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="h-2 w-full overflow-hidden rounded-sm bg-surface-sunken">
            <div
              className={`h-full rounded-sm transition-all ${gaugeColor}`}
              style={{ width: `${overallScore}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between text-xs text-ink-muted">
            <span>0</span>
            <span>30</span>
            <span>60</span>
            <span>80</span>
            <span>100</span>
          </div>
        </div>
      </div>

      {/* Factor breakdown */}
      <div className="rounded-sm border border-hairline bg-surface-raised p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase text-ink-muted">
          Risk Factor Breakdown
        </h3>
        {factors.length === 0 ? (
          <p className="text-sm text-ink-muted">No risk factors assessed yet.</p>
        ) : (
          <div className="space-y-4">
            {factors.map((factor) => (
              <div key={factor.category}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-medium text-ink">
                    {CATEGORY_LABELS[factor.category] ?? factor.category}
                  </span>
                  <span className="text-sm font-mono text-ink-muted">
                    {factor.score}/100
                    <span className="ms-1 text-xs text-ink-muted">
                      (w: {factor.weight.toFixed(2)})
                    </span>
                  </span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-sm bg-surface-sunken">
                  <div
                    className={`h-full rounded-sm transition-all ${getBarColor(factor.score)}`}
                    style={{ width: `${factor.score}%` }}
                  />
                </div>
                {factor.details && (
                  <p className="mt-0.5 text-xs text-ink-muted">{factor.details}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
